import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Crown, ShieldCheck, Users, DollarSign, Tag, TrendingUp,
  Plus, Trash2, Edit2, Globe, BarChart3, ChevronRight,
  Copy, Check, X, Star, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';

function CreatorRegistration({ onRegistered }) {
  const { token, API } = useAuth();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/creator/register`, { creator_name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRegistered(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="p-5 flex flex-col items-center justify-center min-h-[450px]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center space-y-6"
      >
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center">
          <Crown className="w-8 h-8 text-amber-500" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl text-white mb-1">Become a Creator</h2>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Set up your creator profile to track your protected links, manage your discount codes, and see your supporter analytics.
          </p>
        </div>
        <form onSubmit={handleRegister} className="space-y-3 text-left">
          <div>
            <Label className="text-xs text-zinc-500">Creator Name</Label>
            <Input
              data-testid="creator-register-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. TechReviewPro"
              className="bg-[#09090B] border-white/10 text-white mt-1"
              required
            />
            <p className="text-[9px] text-zinc-600 mt-1">This should match the name associated with your affiliate links</p>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="w-full honey-btn px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="creator-register-submit"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Activate Creator Dashboard
              </>
            )}
          </button>
        </form>
        <div className="space-y-2 pt-2">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">What you get</p>
          <div className="grid grid-cols-1 gap-1.5">
            {[
              { icon: BarChart3, text: 'Link performance analytics' },
              { icon: Users, text: 'See who supports your links' },
              { icon: Tag, text: 'Manage your discount codes' },
              { icon: DollarSign, text: 'Revenue impact tracking' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.02]">
                <item.icon className="w-3.5 h-3.5 text-amber-500/70" />
                <span className="text-[11px] text-zinc-400">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function OverviewTab({ stats }) {
  if (!stats) return null;
  const metrics = [
    { icon: ShieldCheck, label: 'Links Protected', value: stats.total_links, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: Users, label: 'Supporters', value: stats.unique_supporters, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { icon: DollarSign, label: 'Est. Revenue', value: `$${stats.estimated_revenue}`, color: 'text-green-400', bg: 'bg-green-500/10' },
    { icon: Tag, label: 'Code Uses', value: stats.total_code_uses, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  ];

  // Find peak day
  const peakDay = stats.timeline?.reduce((max, d) => d.links > max.links ? d : max, { links: 0 });
  const totalRecent = stats.timeline?.reduce((sum, d) => sum + d.links, 0) || 0;

  return (
    <div className="space-y-4">
      {/* Metrics */}
      <div className="grid grid-cols-2 gap-2">
        {metrics.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.06 }}
            className="glass-card rounded-xl p-3"
            data-testid={`creator-metric-${i}`}
          >
            <div className={`w-7 h-7 rounded-lg ${m.bg} flex items-center justify-center mb-1.5`}>
              <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
            </div>
            <p className="font-heading font-black text-lg text-white">{m.value}</p>
            <p className="text-[9px] text-zinc-500">{m.label}</p>
          </motion.div>
        ))}
      </div>

      {/* 30-day chart */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-zinc-400">30-Day Activity</p>
          <span className="text-[9px] text-zinc-600">{totalRecent} links</span>
        </div>
        <div className="flex items-end gap-[2px] h-16">
          {stats.timeline?.map((d, i) => {
            const maxVal = Math.max(...stats.timeline.map(x => x.links), 1);
            const pct = (d.links / maxVal) * 100;
            return (
              <div
                key={i}
                className="flex-1 rounded-t-sm bg-amber-500/40 hover:bg-amber-500/70 transition-colors cursor-default"
                style={{ height: `${Math.max(pct, 2)}%` }}
                title={`${d.date}: ${d.links} links`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[8px] text-zinc-700">30d ago</span>
          <span className="text-[8px] text-zinc-700">Today</span>
        </div>
      </div>

      {/* Top Domains */}
      {stats.top_domains?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-zinc-400 mb-2">Top Sites</p>
          <div className="space-y-1.5">
            {stats.top_domains.slice(0, 4).map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-3 glass-card rounded-lg px-3 py-2.5"
                data-testid={`creator-domain-${i}`}
              >
                <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center">
                  <Globe className="w-3 h-3 text-zinc-400" />
                </div>
                <span className="text-xs text-zinc-300 flex-1 truncate">{d.domain}</span>
                <span className="text-xs font-mono font-bold text-amber-500">{d.count}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Top Supporters */}
      {stats.top_supporters?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-zinc-400 mb-2">Top Supporters</p>
          <div className="space-y-1.5">
            {stats.top_supporters.slice(0, 5).map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-3 glass-card rounded-lg px-3 py-2.5"
                data-testid={`creator-supporter-${i}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${
                  i === 0 ? 'bg-amber-500/20 text-amber-400' : 
                  i === 1 ? 'bg-zinc-400/20 text-zinc-300' : 
                  i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-zinc-500'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{s.name}</p>
                  <p className="text-[9px] text-zinc-600">Level {s.level}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono font-bold text-amber-500">{s.links_protected}</p>
                  <p className="text-[8px] text-zinc-600">links</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CodesTab({ codes, onRefresh, token, API }) {
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [form, setForm] = useState({
    code: '', store: '', store_domain: '',
    discount_type: 'percentage', discount_value: '', description: ''
  });

  const resetForm = () => {
    setForm({ code: '', store: '', store_domain: '', discount_type: 'percentage', discount_value: '', description: '' });
    setEditingCode(null);
  };

  const openEdit = (c) => {
    setForm({
      code: c.code,
      store: c.store,
      store_domain: c.store_domain,
      discount_type: c.discount_type || 'percentage',
      discount_value: String(c.discount_value || ''),
      description: c.description || ''
    });
    setEditingCode(c);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      discount_value: form.discount_value ? parseFloat(form.discount_value) : 0,
    };
    try {
      if (editingCode) {
        await axios.put(`${API}/creator/codes/${editingCode.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/creator/codes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      setShowForm(false);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save code');
    }
    setSaving(false);
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Delete this code?')) return;
    try {
      await axios.delete(`${API}/creator/codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const toggleActive = async (code) => {
    try {
      await axios.put(`${API}/creator/codes/${code.id}`, { active: !code.active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (e) {
      alert('Failed to update');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-zinc-400">{codes.length} Code{codes.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="honey-btn px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5"
          data-testid="creator-add-code-btn"
        >
          <Plus className="w-3 h-3" />
          Add Code
        </button>
      </div>

      {/* Codes List */}
      {codes.length === 0 ? (
        <div className="text-center py-10 glass-card rounded-xl">
          <Tag className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-500">No codes yet</p>
          <p className="text-[10px] text-zinc-600 mt-1">Add your first discount or affiliate code</p>
        </div>
      ) : (
        <div className="space-y-2">
          {codes.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="glass-card rounded-xl p-3"
              data-testid={`creator-code-${i}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyCode(c.code)}
                    className="font-mono font-bold text-sm text-white bg-white/5 px-2.5 py-1 rounded-md hover:bg-white/10 transition-colors flex items-center gap-1.5"
                    data-testid={`copy-code-${i}`}
                  >
                    {c.code}
                    {copiedId === c.code ? (
                      <Check className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <Copy className="w-3 h-3 text-zinc-500" />
                    )}
                  </button>
                  {c.verified && (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Verified</span>
                  )}
                  {!c.verified && (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Pending</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`edit-code-${i}`}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteCode(c.id)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-code-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span className="text-zinc-300">{c.store}</span>
                <span>{c.store_domain}</span>
                {c.discount_value > 0 && (
                  <span className="text-purple-400 font-mono">
                    {c.discount_value}{c.discount_type === 'percentage' ? '%' : '$'} off
                  </span>
                )}
                <span className="ml-auto font-mono text-zinc-400">{c.uses || 0} uses</span>
              </div>
              {c.description && (
                <p className="text-[9px] text-zinc-600 mt-1.5">{c.description}</p>
              )}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                <span className="text-[9px] text-zinc-600">
                  {c.active ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={c.active}
                  onCheckedChange={() => toggleActive(c)}
                  className="data-[state=checked]:bg-amber-500 scale-75 origin-right"
                  data-testid={`toggle-code-${i}`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-base">
              {editingCode ? 'Edit Code' : 'Add New Code'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-zinc-500">Code *</Label>
              <Input
                data-testid="creator-code-input"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SAVE20"
                className="bg-[#09090B] border-white/10 text-white mt-1 font-mono"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-500">Store *</Label>
                <Input
                  data-testid="creator-code-store"
                  value={form.store}
                  onChange={e => setForm({ ...form, store: e.target.value })}
                  placeholder="Amazon"
                  className="bg-[#09090B] border-white/10 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Domain *</Label>
                <Input
                  data-testid="creator-code-domain"
                  value={form.store_domain}
                  onChange={e => setForm({ ...form, store_domain: e.target.value })}
                  placeholder="amazon.com"
                  className="bg-[#09090B] border-white/10 text-white mt-1"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-500">Discount Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-[#09090B] border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="percentage" className="text-white">% Off</SelectItem>
                    <SelectItem value="fixed" className="text-white">$ Off</SelectItem>
                    <SelectItem value="free_shipping" className="text-white">Free Ship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Value</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.discount_value}
                  onChange={e => setForm({ ...form, discount_value: e.target.value })}
                  placeholder="20"
                  className="bg-[#09090B] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Save on first order..."
                className="bg-[#09090B] border-white/10 text-white mt-1"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="flex-1 px-3 py-2 rounded-lg text-xs bg-white/5 text-zinc-400 hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 honey-btn px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
                data-testid="creator-code-submit"
              >
                {saving ? 'Saving...' : editingCode ? 'Update' : 'Add Code'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CreatorDashboardPage() {
  const { user, token, API, refreshUser } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCreator = user?.is_creator;

  const fetchDashboard = useCallback(async () => {
    if (!isCreator) { setLoading(false); return; }
    try {
      const [dashRes, codesRes] = await Promise.all([
        axios.get(`${API}/creator/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/creator/codes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(dashRes.data);
      setCodes(codesRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [API, token, isCreator]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleRegistered = async () => {
    await refreshUser();
    setLoading(true);
    // Small delay to let state update
    setTimeout(() => fetchDashboard(), 300);
  };

  if (!isCreator) {
    return <CreatorRegistration onRegistered={handleRegistered} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'codes', label: 'My Codes', icon: Tag },
  ];

  return (
    <div className="p-5 space-y-4" data-testid="creator-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center">
          <Crown className="w-4.5 h-4.5 text-amber-500" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-base text-white">Creator Hub</h2>
          <p className="text-[10px] text-zinc-500">{user?.creator_name || 'Creator'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-all ${
              tab === t.id
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`creator-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.15 }}
        >
          {tab === 'overview' && <OverviewTab stats={stats} />}
          {tab === 'codes' && <CodesTab codes={codes} onRefresh={fetchDashboard} token={token} API={API} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
